<template>
    <b-card class="grid-empty-state text-center my-3 border shadow-none" data-test="grid-empty-state">
        <div class="grid-empty-icon-wrap mb-3 mx-auto">
            <slot name="icon">
                <i :class="[displayIcon, 'grid-empty-icon']"></i>
            </slot>
        </div>

        <h5 class="grid-empty-title font-weight-bold mb-2">
            <slot name="title">{{ displayTitle }}</slot>
        </h5>

        <p class="grid-empty-message mb-3 mx-auto">
            <slot name="message">{{ displayMessage }}</slot>
        </p>

        <!-- Suggestions Section -->
        <div v-if="normalizedSuggestions.length > 0" class="grid-empty-suggestions p-3 mb-3 mx-auto rounded text-left">
            <div class="grid-empty-suggestions-header font-weight-bold small mb-2 text-uppercase d-flex align-items-center">
                <i class="bi bi-lightbulb mr-1 text-warning"></i>
                <span>Suggestions to find what you're looking for:</span>
            </div>
            <ul class="grid-empty-suggestions-list mb-0 pl-3 small">
                <li v-for="(sugg, idx) in normalizedSuggestions" :key="idx" class="mb-1">
                    <span>{{ sugg.text }}</span>
                    <b-button
                        v-if="sugg.action"
                        size="sm"
                        variant="link"
                        class="p-0 ml-1 font-weight-bold align-baseline"
                        @click="$emit('action', sugg.action)"
                        :data-test="`btn-sugg-action-${sugg.action}`"
                    >
                        {{ sugg.actionLabel || 'Apply' }} &rarr;
                    </b-button>
                </li>
            </ul>
        </div>

        <!-- Action Affordances -->
        <div class="grid-empty-actions d-flex align-items-center justify-content-center flex-wrap">
            <b-button
                v-if="showReset && hasActiveFilters"
                size="sm"
                :variant="resetVariant"
                class="font-weight-500 px-3 mr-2 mb-1"
                @click="$emit('reset')"
                data-test="btn-empty-reset-filters"
                title="Reset all filters to default criteria"
            >
                <i class="bi bi-arrow-counterclockwise mr-1"></i>{{ resetLabel }}
            </b-button>

            <b-button
                v-for="(act, idx) in actions"
                :key="idx"
                size="sm"
                :variant="act.variant || 'outline-secondary'"
                class="font-weight-500 px-3 mr-2 mb-1"
                @click="$emit('action', act.event || act.action)"
                :data-test="`btn-empty-action-${act.event || act.action || idx}`"
            >
                <i v-if="act.icon" :class="[act.icon, 'mr-1']"></i>{{ act.text || act.label }}
            </b-button>

            <slot name="actions"></slot>
        </div>

        <slot></slot>
    </b-card>
</template>

<script>
export default {
    name: 'GridEmptyState',
    props: {
        title: {
            type: String,
            default: ''
        },
        message: {
            type: String,
            default: ''
        },
        icon: {
            type: String,
            default: ''
        },
        hasActiveFilters: {
            type: Boolean,
            default: false
        },
        suggestions: {
            type: Array,
            default: () => []
        },
        showReset: {
            type: Boolean,
            default: true
        },
        resetLabel: {
            type: String,
            default: 'Reset to Defaults'
        },
        resetVariant: {
            type: String,
            default: 'outline-primary'
        },
        actions: {
            type: Array,
            default: () => []
        }
    },
    computed: {
        displayTitle() {
            if (this.title) return this.title;
            return this.hasActiveFilters ? 'No results match your filters' : 'No items found';
        },
        displayMessage() {
            if (this.message) return this.message;
            return this.hasActiveFilters
                ? 'No records match the current combination of filters, search terms, or date windows.'
                : 'There are no records to display in the current view.';
        },
        displayIcon() {
            if (this.icon) return this.icon;
            return this.hasActiveFilters ? 'bi bi-funnel' : 'bi bi-inbox';
        },
        normalizedSuggestions() {
            if (!Array.isArray(this.suggestions)) return [];
            return this.suggestions
                .filter(Boolean)
                .map(item => {
                    if (typeof item === 'string') {
                        return { text: item, action: null };
                    }
                    return {
                        text: item.text || item.label || '',
                        action: item.action || item.event || null,
                        actionLabel: item.actionLabel || item.btnText || 'Apply'
                    };
                })
                .filter(item => !!item.text);
        }
    }
};
</script>

<style scoped>
.grid-empty-state {
    background-color: var(--vt-card-bg, #fbfcfe);
    border-color: var(--vt-border, #e2e8f0) !important;
    border-radius: 8px;
    padding: 1.5rem 1rem;
}

.grid-empty-icon-wrap {
    width: 52px;
    height: 52px;
    border-radius: 50%;
    background-color: rgba(13, 110, 253, 0.08);
    color: #0d6efd;
    display: flex;
    align-items: center;
    justify-content: center;
}

.grid-empty-icon {
    font-size: 1.5rem;
}

.grid-empty-title {
    color: var(--vt-text, #1e293b);
    font-size: 1.05rem;
}

.grid-empty-message {
    font-size: 0.875rem;
    line-height: 1.5;
    color: var(--vt-muted, #64748b);
    max-width: 580px;
}

.grid-empty-suggestions {
    background-color: rgba(0, 0, 0, 0.025);
    border: 1px dashed rgba(0, 0, 0, 0.12);
    max-width: 560px;
}

.grid-empty-suggestions-header {
    color: var(--vt-muted, #64748b);
    letter-spacing: 0.03em;
    font-size: 0.75rem;
}

.grid-empty-suggestions-list {
    color: var(--vt-text, #334155);
}

.grid-empty-actions {
    gap: 0.35rem;
}

[data-theme="dark"] .grid-empty-state {
    background-color: #1e293b !important;
    border-color: #334155 !important;
}

[data-theme="dark"] .grid-empty-icon-wrap {
    background-color: rgba(56, 189, 248, 0.12);
    color: #38bdf8;
}

[data-theme="dark"] .grid-empty-title {
    color: #f1f5f9;
}

[data-theme="dark"] .grid-empty-message {
    color: #94a3b8 !important;
}

[data-theme="dark"] .grid-empty-suggestions {
    background-color: rgba(255, 255, 255, 0.035);
    border-color: rgba(255, 255, 255, 0.12);
}

[data-theme="dark"] .grid-empty-suggestions-header {
    color: #94a3b8;
}

[data-theme="dark"] .grid-empty-suggestions-list {
    color: #cbd5e1;
}
</style>
